import { CONFIG_OPTION, VALIDATABLE_TYPES } from './constants';
import { classesByType } from './validation-classes';

/**
 * Composes validation class instances from sorted validations
 * @param {object} byType - Validations organized by type
 * @returns {Array} Array of validation class instances
 */
function composeValidations(byType) {
  return Object.entries(byType).map(([type, validations]) => {
    return new classesByType[type](validations);
  });
}

/**
 * Creates forbidden validation entries for an argument
 * @param {string} key - The argument name
 * @param {Array} forbids - The forbidden items
 * @returns {Array} Forbidden items with forbiddingArg added
 */
function createForbiddenEntries(key, forbids) {
  return forbids.map(f => ({
    ...f,
    forbiddingArg: key,
  }));
}

/**
 * Creates the payload for a validation entry
 * @param {string} key - The argument name
 * @param {object} argMeta - The argument metadata
 * @returns {string|object} The validation payload
 */
function createValidationPayload(key, argMeta) {
  return argMeta.options ? { name: key, options: argMeta.options } : key;
}

/**
 * Gets the list name (required or optional) for a validation
 * @param {boolean} isRequired - Whether the argument is required
 * @returns {string} The list name
 */
function getValidationListName(isRequired) {
  return isRequired ? 'required' : 'optional';
}

/**
 * Determines the validation type for an argument
 * @param {object} argMeta - The argument metadata
 * @returns {string} The validation type
 */
function getValidationType(argMeta) {
  return argMeta.options ? CONFIG_OPTION : argMeta.type;
}

/**
 * Sorts metadata into validations by type, organizing them into required, optional, and forbidden lists
 * @param {object} meta - The metadata object containing argument definitions
 * @returns {object} Validations organized by type
 */
function sortValidations(meta) {
  const validationsByType = {};

  Object.entries(meta).forEach(([key, argMeta]) => {
    const type = getValidationType(argMeta);

    if (!VALIDATABLE_TYPES.includes(type)) return;

    const payload = createValidationPayload(key, argMeta);
    validationsByType[type] ??= { required: [], optional: [], forbidden: [] };

    if (argMeta.forbids) {
      const forbiddenEntries = createForbiddenEntries(key, argMeta.forbids);
      validationsByType[type].forbidden.push(...forbiddenEntries);
    }

    const listName = getValidationListName(argMeta.required);
    validationsByType[type][listName].push(payload);
  });

  return validationsByType;
}

/**
 * Generates validation objects from component metadata
 * @param {object} param0 - Object containing meta property
 * @param {object} param0.meta - Metadata object defining component arguments
 * @returns {Array} Array of validation objects
 */
export function generateValidationsFromMetadata({ meta }) {
  if (!meta) {
    return [];
  }

  return composeValidations(sortValidations(meta));
}

export default {
  generateValidationsFromMetadata,
};
